<template>
  <div class="min-h-screen bg-gray-50 py-8">
    <div class="max-w-4xl mx-auto px-4">
      <!-- Header Section -->
      <div class="bg-white rounded-lg shadow-md p-6 mb-6">
        <div class="text-center mb-6">
          <h1 class="text-3xl font-bold text-gray-800 mb-2">Assessment Results</h1>
          <h2 class="text-xl text-gray-600">{{ assessmentResult.assessment?.assessment_name }}</h2>
        </div>

        <!-- Status Banner -->
        <div class="text-center">
          <!-- Show status only if assessment has been attempted -->
          <div v-if="hasValidAssessmentResult()">
            <div v-if="assessmentResult.passmark_met" 
                 class="inline-flex items-center px-6 py-3 rounded-lg bg-green-100 border border-green-200">
              <svg class="w-8 h-8 text-green-600 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" 
                      d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
              </svg>
              <div>
                <div class="text-xl font-bold text-green-800">Congratulations! You Passed!</div>
                <div class="text-green-600">You have successfully completed this assessment</div>
              </div>
            </div>
            
            <div v-else class="inline-flex items-center px-6 py-3 rounded-lg bg-red-100 border border-red-200">
              <svg class="w-8 h-8 text-red-600 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" 
                      d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
              </svg>
              <div>
                <div class="text-xl font-bold text-red-800">Assessment Not Passed</div>
                <div class="text-red-600">You need to retake this assessment</div>
              </div>
            </div>
          </div>
          
          <!-- Show "Not Attempted" state for new assessments -->
          <div v-else class="inline-flex items-center px-6 py-3 rounded-lg bg-gray-100 border border-gray-200">
            <svg class="w-8 h-8 text-gray-600 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" 
                    d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"></path>
            </svg>
            <div>
              <div class="text-xl font-bold text-gray-800">Assessment Not Attempted</div>
              <div class="text-gray-600">You have not taken this assessment yet</div>
            </div>
          </div>
        </div>
      </div>

      <!-- Results Details -->
      <div class="bg-white rounded-lg shadow-md p-6 mb-6">
        <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
          <!-- Score Information -->
          <div class="space-y-4">
            <h3 class="text-lg font-semibold text-gray-800 border-b pb-2">Score Information</h3>
            
            <div class="flex justify-between items-center" v-if="hasValidAssessmentResult()">
              <span class="text-gray-600">Your Score:</span>
              <span class="font-bold text-lg">{{ assessmentResult.exam_result?.score || 0 }}/{{ assessmentResult.assessment?.number_of_questions || 0 }}</span>
            </div>
            <div class="flex justify-between items-center" v-else>
              <span class="text-gray-600">Your Score:</span>
              <span class="font-bold text-lg text-gray-500">Not attempted</span>
            </div>
            
            <div class="flex justify-between items-center" v-if="hasValidAssessmentResult()">
              <span class="text-gray-600">Percentage:</span>
              <span class="font-bold text-lg" 
                    :class="assessmentResult.passmark_met ? 'text-green-600' : 'text-red-600'">
                {{ calculatePercentage() }}%
              </span>
            </div>
            <div class="flex justify-between items-center" v-else>
              <span class="text-gray-600">Percentage:</span>
              <span class="font-bold text-lg text-gray-500">N/A</span>
            </div>
            
            <div class="flex justify-between items-center">
              <span class="text-gray-600">Passmark Required:</span>
              <span class="font-bold text-lg text-gray-800">{{ assessmentResult.assessment?.passmark || 50 }}%</span>
            </div>
            
            <div class="flex justify-between items-center" v-if="hasValidAssessmentResult()">
              <span class="text-gray-600">Status:</span>
              <span class="font-bold px-3 py-1 rounded-full text-sm"
                    :class="assessmentResult.passmark_met ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'">
                {{ assessmentResult.user_assessment_detail?.status || 'Unknown' }}
              </span>
            </div>
            <div class="flex justify-between items-center" v-else>
              <span class="text-gray-600">Status:</span>
              <span class="font-bold px-3 py-1 rounded-full text-sm bg-gray-100 text-gray-800">
                Not Attempted
              </span>
            </div>
          </div>

          <!-- Assessment Details -->
          <div class="space-y-4">
            <h3 class="text-lg font-semibold text-gray-800 border-b pb-2">Assessment Details</h3>
            
            <div class="flex justify-between items-center">
              <span class="text-gray-600">Duration:</span>
              <span class="font-medium">{{ assessmentResult.assessment?.duration }}</span>
            </div>
            
            <div class="flex justify-between items-center">
              <span class="text-gray-600">CPD Accredited:</span>
              <span class="flex items-center">
                <svg v-if="assessmentResult.assessment?.cpd_accredited" class="w-5 h-5 text-green-500 mr-1" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"></path>
                </svg>
                <span class="font-medium" :class="assessmentResult.assessment?.cpd_accredited ? 'text-green-600' : 'text-gray-500'">
                  {{ assessmentResult.assessment?.cpd_accredited ? 'Yes' : 'No' }}
                </span>
              </span>
            </div>
            
            <div class="flex justify-between items-center">
              <span class="text-gray-600">Attempt Number:</span>
              <span class="font-bold text-lg">{{ assessmentResult.current_attempt || 0 }}</span>
            </div>
            
            <div class="flex justify-between items-center" v-if="assessmentResult.max_attempts_allowed">
              <span class="text-gray-600">Max Attempts:</span>
              <span class="font-medium">{{ assessmentResult.max_attempts_allowed }}</span>
            </div>
            
            <div class="flex justify-between items-center" v-if="hasValidAssessmentResult()">
              <span class="text-gray-600">Completed:</span>
              <span class="font-medium">{{ formatDate(assessmentResult.user_assessment_detail?.completed_at) }}</span>
            </div>
            <div class="flex justify-between items-center" v-else>
              <span class="text-gray-600">Completed:</span>
              <span class="font-medium text-gray-500">Not attempted</span>
            </div>
          </div>
        </div>

        <!-- Completion Notes -->
        <div v-if="hasValidAssessmentResult() && assessmentResult.user_assessment_detail?.completion_notes" class="mt-6 pt-6 border-t">
          <h3 class="text-lg font-semibold text-gray-800 mb-3">Assessment Notes</h3>
          <div class="bg-gray-50 rounded-lg p-4">
            <p class="text-gray-700">{{ assessmentResult.user_assessment_detail.completion_notes }}</p>
          </div>
        </div>
      </div>

      <!-- Certificate Information (for passed assessments) -->
      <div v-if="hasValidAssessmentResult() && assessmentResult.passmark_met" class="bg-white rounded-lg shadow-md p-6 mb-6">
        <h3 class="text-lg font-semibold text-gray-800 mb-4">Certificate Information</h3>
        <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div class="flex items-center justify-between">
            <span class="text-gray-600">CPD Points Earned:</span>
            <span class="font-bold text-lg text-green-600">{{ assessmentResult.user_assessment_detail?.cpd_points_earned || '0.0' }}</span>
          </div>
          <div class="flex items-center justify-between">
            <span class="text-gray-600">Total Points:</span>
            <span class="font-bold text-lg">{{ assessmentResult.user_assessment_detail?.total_points || '0.0' }}</span>
          </div>
        </div>
        
        <div v-if="assessmentResult.user_assessment_detail?.certificate_serial_number" class="mt-4 p-4 bg-yellow-50 rounded-lg border border-yellow-200">
          <div class="flex items-center">
            <svg class="w-6 h-6 text-yellow-600 mr-2" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"></path>
            </svg>
            <span class="font-medium text-yellow-800">Certificate Serial Number: {{ assessmentResult.user_assessment_detail.certificate_serial_number }}</span>
          </div>
        </div>
      </div>

      <!-- Action Buttons -->
      <div class="bg-white rounded-lg shadow-md p-6">
        <div class="flex flex-col sm:flex-row gap-4 justify-center items-center">
          <!-- Take Assessment Button (for not attempted assessments) -->
          <button v-if="!hasValidAssessmentResult()" 
                  @click="handleTakeAssessment" 
                  class="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-custom-yellow hover:bg-yellow-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500">
            <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4"></path>
            </svg>
            Take Assessment
          </button>

          <!-- Retake Button (for failed assessments) -->
          <button v-if="shouldShowRetakeButton()" 
                  @click="handleRetake" 
                  :disabled="retaking"
                  class="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-orange-600 hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500 disabled:opacity-50 disabled:cursor-not-allowed">
            <svg v-if="retaking" class="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            <svg v-else class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"></path>
            </svg>
            {{ retaking ? 'Starting Retake...' : `Retake Assessment (Attempt ${(assessmentResult.current_attempt || 0) + 1})` }}
          </button>

          <!-- Back to Assessment Details -->
          <button @click="handleBackToDetails"
                  class="inline-flex items-center px-6 py-3 border border-gray-300 text-base font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500">
            <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7"></path>
            </svg>
            Back to Assessment Details
          </button>

          <!-- View Certificate Button (for passed assessments) -->
          <button v-if="assessmentResult.passmark_met && assessmentResult.user_assessment_detail?.certificate_serial_number"
                  @click="handleViewCertificate"
                  class="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500">
            <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
            </svg>
            View Certificate
          </button>
        </div>

        <!-- Retake Limit Warning -->
        <div v-if="shouldShowRetakeLimitWarning()" class="mt-4 p-4 bg-yellow-50 rounded-lg border border-yellow-200">
          <div class="flex">
            <svg class="w-5 h-5 text-yellow-400 mr-2 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd"></path>
            </svg>
            <div>
              <h3 class="text-sm font-medium text-yellow-800">Retake Limit Reached</h3>
              <p class="mt-1 text-sm text-yellow-700">
                You have reached the maximum number of attempts ({{ assessmentResult.max_attempts_allowed }}) for this assessment.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import api from '@/services/api';
import SweetAlertService from '@/services/SweetAlertService';

export default {
  name: 'AssessmentResultPage',
  data() {
    return {
      assessmentResult: {},
      retaking: false,
      loading: true,
    };
  },
  created() {
    this.assessment_id = this.$route.params.assessment_id;
    this.fetchAssessmentResults();
  },
  methods: {
    async fetchAssessmentResults() {
      try {
        this.loading = true;
        const response = await api.getUserAssessmentResult(this.assessment_id);
        this.assessmentResult = response.assessment_result || response;
        console.log('Assessment results:', this.assessmentResult);
      } catch (error) {
        console.error('Failed to fetch assessment results:', error);
        SweetAlertService.showError('Error', error.message || 'Failed to load assessment results');
        this.$router.push({ name: 'StaffAssessmentDetailsPage', params: { assessment_id: this.assessment_id } });
      } finally {
        this.loading = false;
      }
    },

    calculatePercentage() {
      if (!this.hasValidAssessmentResult() || 
          !this.assessmentResult.exam_result?.score || 
          !this.assessmentResult.assessment?.number_of_questions ||
          this.assessmentResult.assessment.number_of_questions === 0) {
        return 0;
      }
      
      const score = parseInt(this.assessmentResult.exam_result.score) || 0;
      const totalQuestions = parseInt(this.assessmentResult.assessment.number_of_questions) || 0;
      
      if (totalQuestions === 0) return 0;
      
      return Math.round((score / totalQuestions) * 100);
    },

    hasValidAssessmentResult() {
      return this.assessmentResult && 
             this.assessmentResult.exam_result && 
             this.assessmentResult.user_assessment_detail;
    },

    shouldShowRetakeButton() {
      const { user_assessment_detail, retake_eligible, max_attempts_allowed } = this.assessmentResult;
      
      // Don't show retake button if:
      // 1. Assessment was passed (user_assessment_detail.status === 'Complete')
      // 2. User is not eligible for retake (retake_eligible is false)
      // 3. User has reached maximum attempts (if limited)
      
      if (!user_assessment_detail || user_assessment_detail.status === 'Complete') return false;
      
      return user_assessment_detail.status === 'Failed' && 
             retake_eligible && 
             (!max_attempts_allowed || user_assessment_detail.attempt_number < max_attempts_allowed);
    },

    shouldShowRetakeLimitWarning() {
      const { user_assessment_detail, max_attempts_allowed } = this.assessmentResult;
      
      return user_assessment_detail?.status === 'Failed' && 
             max_attempts_allowed && 
             user_assessment_detail.attempt_number >= max_attempts_allowed;
    },

    async handleTakeAssessment() {
      // Navigate to test page for first attempt
      this.$router.push({ 
        name: 'StaffAssessmentTestPage', 
        params: { assessment_id: this.assessment_id }
      });
    },

    async handleRetake() {
      try {
        this.retaking = true;
        
        // Start retake exam directly without confirmation dialog
        await api.createRetakeExam(this.assessment_id);
        
        await SweetAlertService.showSuccess('Success', 'Retake assessment started successfully!');
        
        // Navigate to test page with retake query parameter
        this.$router.push({ 
          name: 'StaffAssessmentTestPage', 
          params: { assessment_id: this.assessment_id },
          query: { retake: 'true' }
        });
      } catch (error) {
        console.error('Failed to start retake:', error);
        
        // Check if error is about already passed assessment
        if (error.message && error.message.includes('already passed') ||
            error.errors && error.errors.some(err => err.includes('already passed'))) {
          // Navigate to results page instead of showing error (refresh current page)
          this.$router.push({
            name: 'AssessmentResultPage',
            params: { assessment_id: this.assessment_id }
          });
        } else {
          SweetAlertService.showError('Error', error.message || 'Failed to start retake assessment');
        }
      } finally {
        this.retaking = false;
      }
    },

    handleBackToDetails() {
      this.$router.push({ 
        name: 'StaffAssessmentDetailsPage', 
        params: { assessment_id: this.assessment_id } 
      });
    },

    handleViewCertificate() {
      // Navigate to certificate view or download
      SweetAlertService.showInfo('Info', 'Certificate viewing feature will be implemented soon');
    },

    formatDate(dateString) {
      if (!dateString) return 'N/A';
      return new Date(dateString).toLocaleString();
    },
  },
};
</script>

<style scoped>
/* Custom animations and transitions */
.fade-in {
  animation: fadeIn 0.5s ease-in-out;
}

@keyframes fadeIn {
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
}

.status-indicator {
  position: relative;
  overflow: hidden;
}

.status-indicator::before {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent);
  animation: shimmer 2s infinite;
}

@keyframes shimmer {
  0% { left: -100%; }
  100% { left: 100%; }
}
</style>